import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import type { SupaSQBuilding } from '../types/supa';
import { supaBuildingKey } from '../types/supa';

function buildingLabel(b: SupaSQBuilding): string {
  return [
    b.buildingName,
    b.address_street_number && b.address_route ? `${b.address_street_number} ${b.address_route}` : b.address_route,
    b.address_locality,
    b.address_administrative_area_level_1,
    b.address_postal_code,
  ].filter(Boolean).join(', ');
}

type Props = {
  /** Selected building keys (place_id|buildingName — see supaBuildingKey). */
  value: string[];
  onChange: (buildingKeys: string[]) => void;
};

/** Multi-select over the Supa Networks serviced-buildings list, for targeting
 * an outage at one or more buildings. Unlike SupaBuildingUnitPicker, this has
 * no unit/service-qualification step — outages target whole buildings. */
export default function SupaBuildingMultiPicker({ value, onChange }: Props) {
  const [buildings, setBuildings] = useState<SupaSQBuilding[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api.adminSupaServicedBuildings()
      .then((data) => {
        setBuildings(data.servicedBuildings ?? []);
        setLoading(false);
      })
      .catch(() => {
        setLoadError('Could not load serviced buildings list');
        setLoading(false);
      });
  }, []);

  const byKey = new Map(buildings.map((b) => [supaBuildingKey(b), b]));
  const selected = value.map((k) => byKey.get(k)).filter((b): b is SupaSQBuilding => !!b);
  // Keys selected before the building list finished loading (e.g. editing an
  // existing outage) won't resolve to a building yet — keep them as plain
  // chips rather than silently dropping them.
  const unresolvedKeys = value.filter((k) => !byKey.has(k));

  if (loadError) {
    return <Alert severity="warning">{loadError}</Alert>;
  }

  return (
    <Box>
      <Autocomplete
        multiple
        options={buildings}
        value={selected}
        loading={loading}
        filterOptions={(options, state) => {
          const q = state.inputValue.toLowerCase().trim();
          if (!q) return options.slice(0, 100);
          const results = options.filter((b) => {
            const haystack = [
              b.buildingName,
              b.address_subpremise,
              b.address_street_number,
              b.address_route,
              `${b.address_street_number ?? ''} ${b.address_route ?? ''}`.trim(),
              b.address_locality,
              b.address_postal_code,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
          });
          return results.slice(0, 30);
        }}
        getOptionLabel={buildingLabel}
        isOptionEqualToValue={(a, b) => supaBuildingKey(a) === supaBuildingKey(b)}
        onChange={(_, items) => onChange(items.map(supaBuildingKey))}
        noOptionsText={loading ? 'Loading…' : 'No matching buildings — try a street name or suburb'}
        renderOption={(props, b) => (
          <li {...props} key={supaBuildingKey(b)}>
            <Box sx={{ py: 0.25 }}>
              <Typography variant="body2" fontWeight={600}>{b.buildingName.replace(/[\r\n]+/g, ' — ')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[b.address_street_number, b.address_route, b.address_locality, b.address_administrative_area_level_1, b.address_postal_code].filter(Boolean).join(' ')}
              </Typography>
            </Box>
          </li>
        )}
        renderTags={(items, getTagProps) =>
          items.map((b, index) => (
            <Chip
              {...getTagProps({ index })}
              key={supaBuildingKey(b)}
              label={b.buildingName.replace(/[\r\n]+/g, ' — ')}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Impacted buildings"
            placeholder="Search building, street or suburb…"
            InputProps={{
              ...params.InputProps,
              // params.InputProps.startAdornment carries the selected-building
              // chips in multi-select mode — keep it, don't replace it.
              startAdornment: (
                <>
                  <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {unresolvedKeys.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {unresolvedKeys.map((k) => (
            <Chip
              key={k}
              label={k.split('|')[1] || k}
              size="small"
              onDelete={() => onChange(value.filter((v) => v !== k))}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
